import React, { useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';

import { getFlatTypeSlug } from '../../common/helpers/flatType.utils';
import {
  generateUrlFromSearchOption,
  SearchOption,
} from '../../common/helpers/searchOption.utils';
import {
  canonizeSearchQuery,
  slugifySearchQuery,
} from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import { IMapArea } from '../../common/model/mapArea.model';
import { FlatType } from '../../common/model/enums/flatType.enum';
import { I18nContextType } from '../../libs/i18n';

import BreadcrumbDropdown from './breadcrumbDropdown';
import { SEARCH_BY_COUNTY_KEYWORD } from '../../common/consts';

type Props = {
  searchOptions: SearchOption[];
  mapArea?: IMapArea;
  county?: string;
  city?: string;
  zone?: string;
  flatTitle?: string;
  flatType?: string;
};

type ItemType = {
  label: string;
  county?: string;
  city?: string;
  zone?: string;
  href?: string;
};

const FlatBreadcrumb: React.FC<Props> = ({
  searchOptions,
  mapArea,
  county,
  city,
  zone,
  flatTitle,
  flatType,
}) => {
  const router = useRouter();
  const i18n = useI18n();

  const items: ItemType[] = useMemo(
    () =>
      getBreadcrumbItems(
        i18n,
        flatType,
        county,
        city,
        zone,
        flatTitle,
        mapArea
      ),
    [city, county, flatTitle, flatType, i18n, mapArea, zone]
  );
  const lastItem = useMemo(() => items[items.length - 1], [items]);

  const dropdownItems = useMemo(() => {
    const filteredOptions = searchOptions.filter((option) => {
      if (option.text === lastItem.label) {
        return false;
      }

      if (!lastItem.county) {
        return !option.city;
      } else if (!lastItem.city) {
        return (
          lastItem.county.includes(slugifySearchQuery(option.county)) &&
          !option.zone
        );
      } else if (!lastItem.zone) {
        return (
          !!option.city &&
          lastItem.city === slugifySearchQuery(option.city) &&
          !!option.zone
        );
      }
      return false;
    });
    return filteredOptions.map((option) => ({
      key: option.text,
      label: (
        <Link
          href={generateUrlFromSearchOption(option, true).replace(
            getFlatTypeSlug(FlatType.PISO, true),
            flatType
          )}
          legacyBehavior
        >
          <a>{option.text}</a>
        </Link>
      ),
    }));
  }, [
    flatType,
    lastItem.city,
    lastItem.county,
    lastItem.label,
    lastItem.zone,
    searchOptions,
  ]);

  const getRegularElement = useCallback((label: string, href?: string) => {
    let span = <span>{label}</span>;
    if (href === '/') {
      span = <HomeOutlined alt={label} />;
    }
    return (
      <Link key={`breadcrumb-link-${label}`} href={href} legacyBehavior>
        <a>{span}</a>
      </Link>
    );
  }, []);

  const getLastElement = useCallback(
    (item: ItemType) => {
      const span = <span>{item.label}</span>;
      if (dropdownItems.length === 0) {
        return span;
      }
      return <BreadcrumbDropdown items={dropdownItems} label={span} />;
    },
    [dropdownItems]
  );

  const breadcrumbItems = useMemo(() => {
    return items.map((item, i) => (
      <Breadcrumb.Item key={`breadcrumb-item-${item.label}`}>
        {i === items.length - 1
          ? getLastElement(item)
          : getRegularElement(item.label, item.href)}
      </Breadcrumb.Item>
    ));
  }, [getLastElement, getRegularElement, items]);

  return (
    <div>
      <Breadcrumb>{breadcrumbItems}</Breadcrumb>
    </div>
  );
};

export default FlatBreadcrumb;

export const getBreadcrumbItems = (
  i18n: I18nContextType,
  flatType: string,
  county?: string,
  city?: string,
  zone?: string,
  flatTitle?: string,
  mapArea?: IMapArea
): ItemType[] => {
  let result: ItemType[] = [
    { label: i18n.t('header.home'), href: '/' },
    {
      label: i18n.t('header.buyHouse'),
      href: '/comprar',
    },
  ];
  if (county) {
    const slugifiedCounty = slugifySearchQuery(county);
    const provinceSuffix = i18n.t('flat.county').toLowerCase();

    let label =
      i18n.t(`dynamic.searchPageUrls2.${slugifiedCounty}.locationName`) ||
      county;
    if (
      !label.toLowerCase().includes(provinceSuffix) &&
      !label.toLowerCase().includes(SEARCH_BY_COUNTY_KEYWORD)
    ) {
      label += ` ${SEARCH_BY_COUNTY_KEYWORD}`;
    }

    result.push({
      label,
      county,
      href: `/comprar/${flatType}/${slugifiedCounty}`,
    });

    if (city) {
      const slugifiedCity = slugifySearchQuery(city);
      result.push({
        label:
          i18n.t(
            `dynamic.searchPageUrls2.${slugifiedCounty}/${slugifiedCity}.locationName`
          ) || city,
        county,
        city,
        href: `/comprar/${flatType}/${slugifiedCounty}/${slugifiedCity}`,
      });

      if (zone) {
        const slugifiedZone = slugifySearchQuery(zone);
        result.push({
          label:
            i18n.t(
              `dynamic.searchPageUrls2.${slugifiedCounty}/${slugifiedCity}/${slugifiedZone}.locationName`
            ) || zone,
          county,
          city,
          zone,
          href: `/comprar/${flatType}/${slugifiedCounty}/${slugifiedCity}/${slugifiedZone}`,
        });

        if (flatTitle) {
          result.push({
            label:
              flatTitle.substring(0, 50) + (flatTitle.length > 50 ? '...' : ''),
            county,
            city,
            zone,
          });
        }
      }
    }
  } else if (mapArea) {
    result.push({
      label: `Área ${canonizeSearchQuery(mapArea.id)}`,
      href: undefined,
    });
  }
  return result;
};
